import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { CheckSquare, Calendar, User, AlertCircle, Archive, Edit, Trash2, UserPlus, CheckCircle, ArrowUpDown, Filter } from 'lucide-react';
import AttachmentPreview from './AttachmentPreview';

const PRIORITY_COLORS = {
  'Urgent': 'bg-red-100 text-red-800',
  'High': 'bg-orange-100 text-orange-800',
  'Low': 'bg-blue-100 text-blue-800'
};

const TaskHub = ({ teamMembers = [], onTaskUpdate, onAddTask, onClearClientFilter, refreshTrigger, clientFilter = null, title = 'Task Hub' }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('due_date');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeSubtasks, setIncludeSubtasks] = useState(false);
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canFilterAssignee, setCanFilterAssignee] = useState(false);
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const assigneeMenuRef = useRef(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // Open attachment preview
  const openAttachmentPreview = (attachment) => {
    console.log('🔍 TaskHub - Opening attachment preview:', attachment);
    setPreviewAttachment(attachment);
    setShowPreview(true);
  };

  // Close attachment preview
  const closeAttachmentPreview = () => {
    setShowPreview(false);
    setPreviewAttachment(null);
  };

  useEffect(() => {
    fetchTasks();
  }, [sortBy, includeCompleted, includeSubtasks, assignedToMe, selectedAssigneeIds.join(','), includeUnassigned, refreshTrigger, clientFilter?.id]);

  // Listen for task changes to refresh the list
  useEffect(() => {
    const handleTaskDeleted = () => {
      console.log('🗑️ TaskHub: Task deleted event received');
      fetchTasks(true);
    };

    const handleTaskUpdated = () => {
      console.log('🔄 TaskHub: Task updated/created event received');
      fetchTasks(true);
    };
    
    window.addEventListener('taskDeleted', handleTaskDeleted);
    window.addEventListener('taskUpdated', handleTaskUpdated);
    return () => {
      window.removeEventListener('taskDeleted', handleTaskDeleted);
      window.removeEventListener('taskUpdated', handleTaskUpdated);
    };
  }, []);

  // Close assignee filter popover on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!showAssigneeMenu) return;
      if (assigneeMenuRef.current && !assigneeMenuRef.current.contains(event.target)) {
        setShowAssigneeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showAssigneeMenu]);

  const fetchTasks = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Add small delay if force refresh to ensure DB transaction is committed
      if (forceRefresh) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      const response = await api.get('/tasks/hub', {
        params: { 
          sort_by: sortBy, 
          include_completed: includeCompleted,
          include_subtasks: includeSubtasks,
          assigned_to_me: assignedToMe,
          client_id: clientFilter?.id || undefined,
          assignee_user_ids: selectedAssigneeIds.length ? selectedAssigneeIds.join(',') : undefined,
          include_unassigned: includeUnassigned || undefined,
          _t: forceRefresh ? Date.now() : undefined // Cache busting
        }
      });
      
      setTasks(response.data.tasks);
      setIsAdmin(response.data.is_admin);
      setCanFilterAssignee(!!response.data.can_filter_assignee);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      alert('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action, task) => {
    try {
      switch (action) {
        case 'complete':
          try {
            // Mark as complete by moving to final stage of the project
            if (!task.project_id) {
              alert('Cannot complete task without a project');
              return;
            }
            
            console.log('Completing task:', task.id, 'in project:', task.project_id);
            
            // Get full project details with stages from database directly
            const projectResponse = await api.get(`/projects/${task.project_id}`);
            
            console.log('Project response:', projectResponse.data);
            
            // Query stages with full details from project_stages table
            const stagesResponse = await api.get(`/projects/${task.project_id}/stages-full`);
            
            console.log('Stages full response:', stagesResponse.data);
            
            const stagesList = stagesResponse.data;
            if (!stagesList || stagesList.length === 0) {
              alert('Project has no stages configured');
              return;
            }
            
            // Find the last stage (highest stage_order)
            const finalStage = stagesList.reduce((max, stage) => 
              stage.stage_order > max.stage_order ? stage : max
            , stagesList[0]);
            
            console.log('Moving to final stage:', finalStage);
            
            // Update task to move to final stage
            // Include essential fields to avoid null constraint violations
            await api.put(`/tasks/${task.id}`, {
              name: task.name,
              description: task.description,
              stage_id: finalStage.id,
              status: finalStage.name,
              priority: task.priority,
              assignees: task.assignees || [],
              due_date: task.due_date,
              estimated_effort: task.estimated_effort,
              project_id: task.project_id,
              client_id: task.client_id
            });
            
            // Optimistically remove from UI (already implemented in previous updates)
            setTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
            
            // Show success message without page reload
            const successDiv = document.createElement('div');
            successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
            successDiv.textContent = '✓ Task completed successfully';
            document.body.appendChild(successDiv);
            setTimeout(() => successDiv.remove(), 3000);
          } catch (error) {
            console.error('Completion error:', error);
            alert(`Failed to complete task: ${error.response?.data?.detail || error.message}`);
          }
          break;
          
        case 'archive':
          // Optimistically remove from UI
          setTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
          
          await api.put(`/tasks/${task.id}/archive`, {});
          
          // Show success message without page reload
          const archiveSuccessDiv = document.createElement('div');
          archiveSuccessDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
          archiveSuccessDiv.textContent = '✓ Task archived successfully';
          document.body.appendChild(archiveSuccessDiv);
          setTimeout(() => archiveSuccessDiv.remove(), 3000);
          break;
          
        case 'delete':
          if (window.confirm('Permanently delete this task? This cannot be undone.')) {
            // Optimistically remove from UI
            setTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
            
            await api.delete(`/tasks/${task.id}`);
            
            // Show success message without page reload
            const deleteSuccessDiv = document.createElement('div');
            deleteSuccessDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
            deleteSuccessDiv.textContent = '✓ Task deleted successfully';
            document.body.appendChild(deleteSuccessDiv);
            setTimeout(() => deleteSuccessDiv.remove(), 3000);
          }
          break;
          
        case 'assign':
          setSelectedTask(task);
          setShowAssignModal(true);
          break;
          
        case 'edit':
          // Fetch full task details before opening modal
          try {
            const taskDetailsResponse = await api.get(`/tasks/${task.id}`);
            
            if (onTaskUpdate) {
              onTaskUpdate(taskDetailsResponse.data);
            }
          } catch (error) {
            console.error('Error fetching task details:', error);
            alert('Failed to load task details');
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error(`Error with ${action}:`, error);
      alert(`Failed to ${action} task`);
    }
  };

  const handleAssignUsers = async (userIds) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/api/tasks/${selectedTask.id}`, {
        ...selectedTask,
        assignees: userIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Task assigned successfully!');
      setShowAssignModal(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Error assigning task:', error);
      alert('Failed to assign task');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    
    // Parse date as LOCAL date (avoid UTC conversion)
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const tomorrow = new Date(todayOnly);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === todayOnly.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getAssigneeNames = (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return 'Unassigned';
    
    const names = assigneeIds
      .map(id => {
        const member = teamMembers.find(m => m.user?.id === id || m.user_id === id);
        return member?.user?.name || member?.name;
      })
      .filter(Boolean);
    
    if (names.length === 0) return 'Unassigned';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(' & ');
    return `${names[0]} +${names.length - 1}`;
  };

  const stripHtmlAndFormat = (html) => {
    if (!html) return '';
    
    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Replace div and p tags with line breaks before extracting text
    const divs = temp.querySelectorAll('div, p');
    divs.forEach(div => {
      const br = document.createElement('br');
      div.parentNode?.insertBefore(br, div);
      div.replaceWith(div.textContent + ' ');
    });
    
    // Get text content (this strips all HTML tags)
    let text = temp.textContent || temp.innerText || '';
    
    // Clean up multiple line breaks and spaces
    text = text.replace(/\n\s*\n/g, '\n').replace(/\s+/g, ' ').trim();
    
    return text;
  };

  return (
    <div className="flex-1 min-w-0 w-full min-h-screen bg-gray-50 p-4 sm:p-6 overflow-x-auto">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <CheckSquare className="w-8 h-8 text-purple-600" />
                {title}{clientFilter?.name ? ` • ${clientFilter.name}` : ''}
              </h1>
              <p className="text-gray-600 mt-2">
                {clientFilter?.name
                  ? 'Open tasks for this client'
                  : (isAdmin
                    ? (assignedToMe ? 'Tasks assigned to you' : 'All team tasks')
                    : 'Your assigned tasks')} • {tasks.length} task{tasks.length !== 1 ? 's' : ''}
              </p>
              {clientFilter?.name && onClearClientFilter && (
                <button
                  onClick={onClearClientFilter}
                  className="mt-2 text-sm text-purple-700 hover:text-purple-900 underline"
                >
                  Clear client filter
                </button>
              )}
            </div>
            
            <div className="flex flex-wrap gap-3">
              {onAddTask && (
                <button
                  onClick={onAddTask}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  + Add Task
                </button>
              )}

              {/* Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="due_date">Sort by Due Date</option>
                <option value="client">Sort by Client</option>
                <option value="priority">Sort by Priority</option>
              </select>

              {canFilterAssignee && (
                <div className="relative" ref={assigneeMenuRef}>
                  <button
                    onClick={() => setShowAssigneeMenu(prev => !prev)}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-sm"
                  >
                    Assignees{selectedAssigneeIds.length || includeUnassigned ? ` (${selectedAssigneeIds.length + (includeUnassigned ? 1 : 0)})` : ''}
                  </button>
                  {showAssigneeMenu && (
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={includeUnassigned}
                          onChange={(e) => setIncludeUnassigned(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        Unassigned
                      </label>
                      <div className="max-h-56 overflow-y-auto space-y-2 pt-2 border-t border-gray-100">
                        {teamMembers.map((member) => {
                          const userId = member.user?.id || member.user_id;
                          const userName = member.user?.name || member.name || 'Unknown';
                          const checked = selectedAssigneeIds.includes(userId);
                          return (
                            <label key={userId} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setSelectedAssigneeIds(prev => e.target.checked
                                    ? [...prev, userId]
                                    : prev.filter(id => id !== userId));
                                }}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                              <span className="truncate">{userName}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="pt-2 border-t border-gray-100 flex justify-between">
                        <button
                          onClick={() => {
                            setSelectedAssigneeIds([]);
                            setIncludeUnassigned(false);
                          }}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear
                        </button>
                        <button
                          onClick={() => setShowAssigneeMenu(false)}
                          className="text-xs text-purple-700 hover:text-purple-900"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Admin: Show Assigned to Me Toggle */}
              {isAdmin && (
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={assignedToMe}
                    onChange={(e) => setAssignedToMe(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Assigned to Me Only</span>
                </label>
              )}
              
              {/* Toggle Completed */}
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={includeCompleted}
                  onChange={(e) => setIncludeCompleted(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Show Completed</span>
              </label>
              
              {/* Toggle Subtasks */}
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={includeSubtasks}
                  onChange={(e) => setIncludeSubtasks(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Show Subtasks</span>
              </label>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading tasks...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && tasks.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600">
              {includeCompleted 
                ? 'No tasks to display.' 
                : 'No open tasks. Toggle "Show Completed" to see completed tasks.'}
            </p>
          </div>
        )}

        {/* Task List */}
        {!loading && tasks.length > 0 && (
          <div className="space-y-2">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleQuickAction('edit', task)}
              >
                {/* Main Info Row */}
                <div className="px-4 py-3 flex items-center gap-4">
                  {/* Checkbox/Complete Indicator */}
                  <div className="flex-shrink-0">
                    {task.stage_completed ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded" />
                    )}
                  </div>

                  {/* Task Name */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{task.name}</h3>
                  </div>

                  {/* Project/Client Info */}
                  <div className="hidden md:flex flex-col items-end text-xs text-gray-500 flex-shrink-0">
                    <div className="font-medium text-gray-700">{task.project_name || '-'}</div>
                    <div>{task.client_name || 'Company Project'}</div>
                  </div>

                  {/* Stage Badge */}
                  <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    task.stage_completed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {task.stage_name || 'No Stage'}
                  </span>

                  {/* Priority Badge */}
                  <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[task.priority] || 'bg-gray-100 text-gray-800'}`}>
                    {task.priority || 'Normal'}
                  </span>

                  {/* Due Date */}
                  <div className="hidden lg:flex items-center gap-2 text-sm text-gray-700 flex-shrink-0">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="whitespace-nowrap">{formatDate(task.due_date)}</span>
                  </div>

                  {/* Assigned To */}
                  <div className="hidden xl:flex items-center gap-2 text-sm text-gray-700 flex-shrink-0 min-w-[120px]">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{getAssigneeNames(task.assignees)}</span>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAction('complete', task); }}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Mark Complete"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAction('assign', task); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Assign Users"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAction('edit', task); }}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAction('archive', task); }}
                      className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAction('delete', task); }}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Description Row (if exists) */}
                {task.description && (
                  <div className="px-4 pb-3 pt-0">
                    <p className="text-sm text-gray-500 line-clamp-2 pl-9">{stripHtmlAndFormat(task.description)}</p>
                  </div>
                )}

                {/* Mobile-only additional info */}
                <div className="md:hidden px-4 pb-3 pt-0 flex items-center gap-4 text-xs text-gray-500 pl-9">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(task.due_date)}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {getAssigneeNames(task.assignees)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && selectedTask && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Assign Users to "{selectedTask.name}"</h3>
              
              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {teamMembers.map((member) => {
                  const userId = member.user?.id || member.user_id;
                  const userName = member.user?.name || member.name;
                  const isAssigned = selectedTask.assignees?.includes(userId);
                  
                  return (
                    <label key={userId} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={(e) => {
                          const newAssignees = e.target.checked
                            ? [...(selectedTask.assignees || []), userId]
                            : (selectedTask.assignees || []).filter(id => id !== userId);
                          setSelectedTask({ ...selectedTask, assignees: newAssignees });
                        }}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-gray-900">{userName}</span>
                    </label>
                  );
                })}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleAssignUsers(selectedTask.assignees)}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedTask(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachment Preview Modal */}
        <AttachmentPreview
          attachment={previewAttachment}
          isOpen={showPreview}
          onClose={closeAttachmentPreview}
        />
      </div>
    </div>
  );
};

export default TaskHub;
